import React, { memo } from 'react';
import { useScrollLock } from './useScrollLock';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';

import Button, { IButtonProps } from '../Button';
import { ReactComponent as CloseIcon } from 'assets/icons/Clear.svg';

import { useStyles } from './styles';

export interface IModalProps {
  title?: string;
  Content?: React.ReactNode;
  Actions?: React.ReactNode;
  fullScreen?: boolean;
  isVisible?: boolean;
  actionButtons?: IButtonProps[];
  modalStyleClass?: string;
  modalTitleStyleClass?: string;
  modalContentStyleClass?: string;
  modalActionsStyleClass?: string;
  disableScrollLock?: boolean;
  hideCloseIcon?: boolean;
  disableBackdropClose?: boolean;
  testId?: string;
  onClose: () => void;
}

const Modal = ({
  title,
  Content,
  Actions,
  actionButtons,
  fullScreen,
  isVisible = true,
  modalStyleClass = '',
  modalTitleStyleClass = '',
  modalContentStyleClass = '',
  modalActionsStyleClass = '',
  disableScrollLock = true,
  hideCloseIcon = false,
  disableBackdropClose = false,
  testId,
  onClose,
}: IModalProps) => {
  const classes = useStyles();

  useScrollLock(isVisible, disableScrollLock);

  if (!isVisible) {
    return null;
  }

  const safeOnClose = () => onClose && onClose();

  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    safeOnClose();
  };

  return (
    <Dialog
      open={true}
      // fullWidth={true}
      fullScreen={fullScreen}
      data-testid={testId}
      className={`${classes.modal} ${modalStyleClass}`}
      onClose={handleClose}
      disableScrollLock={disableScrollLock}
    >
      <DialogTitle className={`${classes.modalTitle} ${modalTitleStyleClass}`}>
        {title}
        {!hideCloseIcon && (
          <IconButton
            aria-label="close"
            data-testid={testId ? `${testId}-close-button` : undefined}
            className={classes.closeIconParent}
            onClick={safeOnClose}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent className={`${classes.modalContent} ${modalContentStyleClass}`}>{Content}</DialogContent>
      {(Actions || actionButtons?.length > 0) &&
        (Actions || (
          <DialogActions className={`${classes.modalActions} ${modalActionsStyleClass}`}>
            {Actions}
            {actionButtons?.map((buttonProps, index) => (
              <Button key={index} {...buttonProps} styleClass={`${classes.button} ${buttonProps.styleClass || ''}`} />
            ))}
          </DialogActions>
        ))}
    </Dialog>
  );
};

export default memo(Modal);
